/**
 * ResponsiveCanvas - Makes SVG content truly responsive
 * Wraps children and applies scaling/positioning based on viewport size
 */

import React from 'react';
import { ViewportScaling } from './ViewportScaling';

interface ResponsiveCanvasProps {
  children: React.ReactNode;
  width: number;
  height: number;
  referenceWidth?: number;
  referenceHeight?: number;
  scalingMode?: 'proportional' | 'responsive' | 'fluid';
}

export function ResponsiveCanvas({
  children,
  width,
  height,
  referenceWidth = 700,
  referenceHeight = 480,
  scalingMode = 'responsive'
}: ResponsiveCanvasProps) {
  
  const config = {
    containerWidth: width,
    containerHeight: height,
    referenceWidth,
    referenceHeight,
    scalingMode,
    breakpoint: ViewportScaling.getBreakpoint(width) as any
  };
  
  const { scale, scaleX, scaleY } = ViewportScaling.getScaleFactors(config);
  const transform = ViewportScaling.getTransform(config);
  const breakpoint = ViewportScaling.getBreakpoint(width);
  
  // For proportional scaling, we use viewBox to let SVG handle it
  if (scalingMode === 'proportional') {
    return (
      <g transform={transform}>
        {children}
      </g>
    );
  }
  
  // For responsive scaling, we need to pass context down
  // This would ideally use React Context, but for now we'll use transform
  
  // Calculate optimal scale factor based on available space
  let contentScale = 1;
  
  // Calculate scale factors
  const widthScale = width / referenceWidth;
  const heightScale = height / referenceHeight;
  
  switch (breakpoint) {
    case 'mobile':
      // For mobile, prioritize fitting width but ensure readability
      contentScale = Math.max(0.4, Math.min(widthScale, heightScale));
      break;
      
    case 'tablet':
      // For tablet, balance between width and height constraints
      contentScale = Math.min(widthScale, heightScale);
      break;
      
    case 'desktop':
    case 'wide':
      // For desktop, use available space but don't go crazy big
      contentScale = Math.min(widthScale, heightScale);
      // Cap at 2x for readability, but no minimum
      if (contentScale > 2) contentScale = 2;
      break;
  }
  
  const responsiveTransform = `scale(${contentScale})`;
  
  // Center the scaled content if needed
  const scaledWidth = referenceWidth * contentScale;
  const scaledHeight = referenceHeight * contentScale;
  const translateX = scaledWidth < width ? (width - scaledWidth) / 2 : 0;
  const translateY = scaledHeight < height ? (height - scaledHeight) / 2 : 0;
  
  return (
    <g transform={`translate(${translateX}, ${translateY}) ${responsiveTransform}`}>
      {children}
    </g>
  );
}

/**
 * Hook to get responsive values based on viewport
 */
export function useResponsiveValue(width: number) {
  const breakpoint = ViewportScaling.getBreakpoint(width);
  const scale = width / 700; // Reference width
  
  return {
    breakpoint,
    scale,
    fontSize: (base: number) => ViewportScaling.getResponsiveFontSize(base, width),
    spacing: (base: number) => {
      switch (breakpoint) {
        case 'mobile': return base * 0.5;
        case 'tablet': return base * 0.75;
        default: return base * scale;
      }
    },
    elementWidth: (base: number) => {
      switch (breakpoint) {
        case 'mobile': return width - 40; // Full width minus padding
        case 'tablet': return base * 0.8;
        default: return base * scale;
      }
    }
  };
}